import {
  TYPED_REQUEST_REDACTED_PAYLOAD,
  type ITypedRequestLogEntry,
  type ITypedRequestLogMetadata,
  type ITypedRouterHookProjectionOptions,
  type ITypedRouterHooks,
  type TTypedRouterHookCallback,
} from './interfaces.hooks.js';

export type TTypedRouterHookName = keyof ITypedRouterHooks;

export interface ITypedRequestHookSource {
  readonly correlationId: string;
  readonly method: string;
  readonly direction: 'outgoing' | 'incoming';
  readonly phase: 'request' | 'response';
  readonly timestamp: number;
  readonly durationMs?: number;
  readonly payload: unknown;
  readonly errorText?: string;
}

interface IProjectedHookRegistration {
  readonly callback: TTypedRouterHookCallback;
  readonly projectionOptions: ITypedRouterHookProjectionOptions;
}

type TProjectedRegistrationsByPhase = Partial<
  Record<TTypedRouterHookName, IProjectedHookRegistration>
>;

export interface ITypedRouterHookRegistrationState {
  hooks: ITypedRouterHooks;
  readonly projectedRegistrationsByPhase: TProjectedRegistrationsByPhase;
}

interface IRealmGlobalHookState {
  readonly version: '3.8';
  readonly stateShape: 'callback-bound-projections-v1';
  readonly legacy: ITypedRouterHookRegistrationState;
  readonly subscribers: Map<symbol, ITypedRouterHookRegistrationState>;
}

interface IHookDispatchRecipient {
  readonly callback: TTypedRouterHookCallback;
  readonly projectionOptions: ITypedRouterHookProjectionOptions;
}

const hookNames = Object.freeze([
  'onOutgoingRequest',
  'onIncomingResponse',
  'onIncomingRequest',
  'onOutgoingResponse',
] as const satisfies readonly TTypedRouterHookName[]);

const realmGlobalHookStateSlot = Symbol.for(
  '@api.global/typedrequest:global-hooks:3.8'
);

const copyProjectionOptions = (
  projectionOptionsArg?: ITypedRouterHookProjectionOptions
): ITypedRouterHookProjectionOptions => ({
  payloadProjector: projectionOptionsArg?.payloadProjector,
  errorProjector: projectionOptionsArg?.errorProjector,
});

export const createTypedRouterHookRegistrationState = (
  hooksArg: ITypedRouterHooks = {}
): ITypedRouterHookRegistrationState => ({
  hooks: hooksArg,
  projectedRegistrationsByPhase: {},
});

const getSuppliedHookEntries = (
  hooksArg: ITypedRouterHooks
): Array<readonly [TTypedRouterHookName, TTypedRouterHookCallback | undefined]> =>
  hookNames
    .filter((hookNameArg) => Object.hasOwn(hooksArg, hookNameArg))
    .map((hookNameArg) => [hookNameArg, hooksArg[hookNameArg]] as const);

export const mergeTypedRouterHookRegistrations = (
  registrationStateArg: ITypedRouterHookRegistrationState,
  hooksArg: ITypedRouterHooks,
  projectionOptionsArg?: ITypedRouterHookProjectionOptions
): void => {
  const suppliedHookEntries = getSuppliedHookEntries(hooksArg);
  registrationStateArg.hooks = {
    ...registrationStateArg.hooks,
    ...hooksArg,
  };

  for (const [hookName] of suppliedHookEntries) {
    const callback = registrationStateArg.hooks[hookName];
    if (typeof callback !== 'function') {
      delete registrationStateArg.projectedRegistrationsByPhase[hookName];
      continue;
    }
    registrationStateArg.projectedRegistrationsByPhase[hookName] = {
      callback,
      projectionOptions: copyProjectionOptions(projectionOptionsArg),
    };
  }
};

export const replaceTypedRouterHookRegistrations = (
  registrationStateArg: ITypedRouterHookRegistrationState,
  hooksArg: ITypedRouterHooks
): void => {
  registrationStateArg.hooks = hooksArg;
  for (const hookName of hookNames) {
    delete registrationStateArg.projectedRegistrationsByPhase[hookName];
  }
};

const getRealmGlobalHookState = (): IRealmGlobalHookState => {
  const existingState = (globalThis as any)[realmGlobalHookStateSlot] as
    | IRealmGlobalHookState
    | undefined;
  if (
    existingState?.version === '3.8' &&
    existingState.stateShape === 'callback-bound-projections-v1' &&
    existingState.subscribers instanceof Map
  ) {
    return existingState;
  }

  const newState: IRealmGlobalHookState = {
    version: '3.8',
    stateShape: 'callback-bound-projections-v1',
    legacy: createTypedRouterHookRegistrationState(),
    subscribers: new Map(),
  };
  (globalThis as any)[realmGlobalHookStateSlot] = newState;
  return newState;
};

export const getLegacyGlobalHooks = (): ITypedRouterHooks =>
  getRealmGlobalHookState().legacy.hooks;

export const replaceLegacyGlobalHooks = (hooksArg: ITypedRouterHooks): void => {
  replaceTypedRouterHookRegistrations(
    getRealmGlobalHookState().legacy,
    hooksArg
  );
};

export const mergeLegacyGlobalHooks = (
  hooksArg: ITypedRouterHooks,
  projectionOptionsArg?: ITypedRouterHookProjectionOptions
): void => {
  mergeTypedRouterHookRegistrations(
    getRealmGlobalHookState().legacy,
    hooksArg,
    projectionOptionsArg
  );
};

export const addGlobalHookSubscriber = (
  hooksArg: ITypedRouterHooks,
  projectionOptionsArg?: ITypedRouterHookProjectionOptions
): (() => void) => {
  const globalState = getRealmGlobalHookState();
  const subscriberId = Symbol('typedrequest-hook-subscriber');
  const registrationState = createTypedRouterHookRegistrationState();
  mergeTypedRouterHookRegistrations(
    registrationState,
    hooksArg,
    projectionOptionsArg
  );
  globalState.subscribers.set(subscriberId, registrationState);

  let disposed = false;
  return () => {
    if (disposed) {
      return;
    }
    disposed = true;
    globalState.subscribers.delete(subscriberId);
  };
};

export const clearGlobalHookRegistrations = (): void => {
  const globalState = getRealmGlobalHookState();
  replaceTypedRouterHookRegistrations(globalState.legacy, {});
  globalState.subscribers.clear();
};

const logProjectionFailure = (hookNameArg: TTypedRouterHookName): void => {
  try {
    console.error(`TypedRequest hook projection failed (${hookNameArg})`);
  } catch {
    // Logging must not let a hook failure escape into request routing.
  }
};

const logCallbackFailure = (hookNameArg: TTypedRouterHookName): void => {
  try {
    console.error(`TypedRequest hook callback failed (${hookNameArg})`);
  } catch {
    // Logging must not let a hook failure escape into request routing.
  }
};

const createMetadata = (
  sourceArg: ITypedRequestHookSource,
  hookNameArg: TTypedRouterHookName
): Readonly<ITypedRequestLogMetadata> | undefined => {
  const metadata: ITypedRequestLogMetadata = {
    correlationId: sourceArg.correlationId,
    method: sourceArg.method,
    direction: sourceArg.direction,
    phase: sourceArg.phase,
    timestamp: sourceArg.timestamp,
    ...(sourceArg.durationMs === undefined
      ? {}
      : { durationMs: sourceArg.durationMs }),
    hasError: sourceArg.errorText !== undefined,
  };
  try {
    return Object.freeze(metadata);
  } catch {
    logProjectionFailure(hookNameArg);
    return undefined;
  }
};

const isSharedArrayBuffer = (valueArg: object): boolean => {
  if (typeof SharedArrayBuffer === 'undefined') {
    return false;
  }
  return (
    valueArg instanceof SharedArrayBuffer ||
    Object.prototype.toString.call(valueArg) === '[object SharedArrayBuffer]'
  );
};

const assertNoSharedArrayBuffer = (
  valueArg: unknown,
  checkedValuesArg = new Set<object>()
): void => {
  if (
    valueArg === null ||
    (typeof valueArg !== 'object' && typeof valueArg !== 'function')
  ) {
    return;
  }

  if (isSharedArrayBuffer(valueArg)) {
    throw new TypeError('TypedRequest hook projections cannot contain SharedArrayBuffer');
  }
  if (ArrayBuffer.isView(valueArg)) {
    if (isSharedArrayBuffer(valueArg.buffer)) {
      throw new TypeError(
        'TypedRequest hook projections cannot contain SharedArrayBuffer-backed views'
      );
    }
    return;
  }
  if (checkedValuesArg.has(valueArg)) {
    return;
  }
  checkedValuesArg.add(valueArg);

  if (valueArg instanceof Map) {
    for (const [key, value] of valueArg) {
      assertNoSharedArrayBuffer(key, checkedValuesArg);
      assertNoSharedArrayBuffer(value, checkedValuesArg);
    }
    return;
  }
  if (valueArg instanceof Set) {
    for (const value of valueArg) {
      assertNoSharedArrayBuffer(value, checkedValuesArg);
    }
    return;
  }

  for (const propertyKey of Reflect.ownKeys(valueArg)) {
    const descriptor = Reflect.getOwnPropertyDescriptor(valueArg, propertyKey);
    if (!descriptor) {
      continue;
    }
    if (!Object.hasOwn(descriptor, 'value')) {
      throw new TypeError(
        'TypedRequest hook projections cannot contain accessor properties'
      );
    }
    assertNoSharedArrayBuffer(descriptor.value, checkedValuesArg);
  }
};

const snapshotPayload = (
  sourcePayloadArg: unknown,
  metadataArg: Readonly<ITypedRequestLogMetadata> | undefined,
  projectionOptionsArg: ITypedRouterHookProjectionOptions,
  hookNameArg: TTypedRouterHookName
): { payload: unknown; payloadRedacted: boolean } => {
  if (!metadataArg || !projectionOptionsArg.payloadProjector) {
    return {
      payload: TYPED_REQUEST_REDACTED_PAYLOAD,
      payloadRedacted: true,
    };
  }

  try {
    const projectedPayload = projectionOptionsArg.payloadProjector(
      sourcePayloadArg,
      metadataArg
    );
    assertNoSharedArrayBuffer(projectedPayload);
    const payloadSnapshot = structuredClone(projectedPayload);
    assertNoSharedArrayBuffer(payloadSnapshot);
    if (
      payloadSnapshot !== null &&
      (typeof payloadSnapshot === 'object' || typeof payloadSnapshot === 'function')
    ) {
      Object.freeze(payloadSnapshot);
    }
    return {
      payload: payloadSnapshot,
      payloadRedacted: false,
    };
  } catch {
    logProjectionFailure(hookNameArg);
    return {
      payload: TYPED_REQUEST_REDACTED_PAYLOAD,
      payloadRedacted: true,
    };
  }
};

const snapshotError = (
  sourceArg: ITypedRequestHookSource,
  metadataArg: Readonly<ITypedRequestLogMetadata> | undefined,
  projectionOptionsArg: ITypedRouterHookProjectionOptions,
  hookNameArg: TTypedRouterHookName
): string | undefined => {
  if (
    !metadataArg ||
    sourceArg.errorText === undefined ||
    !projectionOptionsArg.errorProjector
  ) {
    return undefined;
  }

  try {
    const projectedError = projectionOptionsArg.errorProjector(
      sourceArg.errorText,
      metadataArg
    );
    if (projectedError === undefined) {
      return undefined;
    }
    const errorSnapshot = structuredClone(projectedError);
    if (typeof errorSnapshot !== 'string') {
      throw new TypeError('TypedRequest error projector must return a string or undefined');
    }
    return errorSnapshot;
  } catch {
    logProjectionFailure(hookNameArg);
    return undefined;
  }
};

const projectLogEntry = (
  sourceArg: ITypedRequestHookSource,
  projectionOptionsArg: ITypedRouterHookProjectionOptions,
  hookNameArg: TTypedRouterHookName
): ITypedRequestLogEntry => {
  const metadata = createMetadata(sourceArg, hookNameArg);
  const projectedPayload = snapshotPayload(
    sourceArg.payload,
    metadata,
    projectionOptionsArg,
    hookNameArg
  );
  const projectedError = snapshotError(
    sourceArg,
    metadata,
    projectionOptionsArg,
    hookNameArg
  );
  const entry: ITypedRequestLogEntry = {
    correlationId: sourceArg.correlationId,
    method: sourceArg.method,
    direction: sourceArg.direction,
    phase: sourceArg.phase,
    timestamp: sourceArg.timestamp,
    ...(sourceArg.durationMs === undefined
      ? {}
      : { durationMs: sourceArg.durationMs }),
    hasError: sourceArg.errorText !== undefined,
    ...projectedPayload,
    ...(projectedError === undefined ? {} : { error: projectedError }),
  };

  try {
    return Object.freeze(entry);
  } catch {
    logProjectionFailure(hookNameArg);
    const fallbackEntry: ITypedRequestLogEntry = {
      correlationId: sourceArg.correlationId,
      method: sourceArg.method,
      direction: sourceArg.direction,
      phase: sourceArg.phase,
      timestamp: sourceArg.timestamp,
      ...(sourceArg.durationMs === undefined
        ? {}
        : { durationMs: sourceArg.durationMs }),
      hasError: sourceArg.errorText !== undefined,
      payload: TYPED_REQUEST_REDACTED_PAYLOAD,
      payloadRedacted: true,
    };
    try {
      return Object.freeze(fallbackEntry);
    } catch {
      return fallbackEntry;
    }
  }
};

const snapshotDispatchRecipient = (
  registrationStateArg: ITypedRouterHookRegistrationState,
  hookNameArg: TTypedRouterHookName
): IHookDispatchRecipient | undefined => {
  try {
    const callback = registrationStateArg.hooks[hookNameArg];
    if (typeof callback !== 'function') {
      return undefined;
    }
    const projectedRegistration =
      registrationStateArg.projectedRegistrationsByPhase[hookNameArg];
    return {
      callback,
      projectionOptions:
        projectedRegistration?.callback === callback
          ? copyProjectionOptions(projectedRegistration.projectionOptions)
          : {},
    };
  } catch {
    logCallbackFailure(hookNameArg);
    return undefined;
  }
};

const dispatchToRecipient = (
  recipientArg: IHookDispatchRecipient,
  hookNameArg: TTypedRouterHookName,
  sourceArg: ITypedRequestHookSource
): void => {
  const entry = projectLogEntry(
    sourceArg,
    recipientArg.projectionOptions,
    hookNameArg
  );
  try {
    const callbackResult = (
      recipientArg.callback as (entryArg: ITypedRequestLogEntry) => unknown
    )(entry);
    if (
      callbackResult &&
      typeof (callbackResult as Promise<void>).then === 'function'
    ) {
      void Promise.resolve(callbackResult).catch(() => {
        logCallbackFailure(hookNameArg);
      });
    }
  } catch {
    logCallbackFailure(hookNameArg);
  }
};

export const dispatchTypedRequestHook = (
  hookNameArg: TTypedRouterHookName,
  sourceArg: ITypedRequestHookSource,
  instanceRegistrationStateArg?: ITypedRouterHookRegistrationState
): void => {
  const globalState = getRealmGlobalHookState();
  if (globalState.subscribers.size === 0) {
    const legacyRecipient = snapshotDispatchRecipient(
      globalState.legacy,
      hookNameArg
    );
    const instanceRecipient = instanceRegistrationStateArg
      ? snapshotDispatchRecipient(instanceRegistrationStateArg, hookNameArg)
      : undefined;
    if (!legacyRecipient && !instanceRecipient) return;
    if (legacyRecipient) {
      dispatchToRecipient(legacyRecipient, hookNameArg, sourceArg);
    }
    if (instanceRecipient) {
      dispatchToRecipient(instanceRecipient, hookNameArg, sourceArg);
    }
    return;
  }
  const registrationStates = [
    globalState.legacy,
    ...Array.from(globalState.subscribers.values()),
    ...(instanceRegistrationStateArg ? [instanceRegistrationStateArg] : []),
  ];
  const recipients = registrationStates
    .map((registrationStateArg) =>
      snapshotDispatchRecipient(registrationStateArg, hookNameArg)
    )
    .filter(
      (recipientArg): recipientArg is IHookDispatchRecipient => !!recipientArg
    );

  for (const recipient of recipients) {
    dispatchToRecipient(recipient, hookNameArg, sourceArg);
  }
};
